import { Box, CircularProgress, Grid, Stack, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { theme } from '../../../theme';
import { getPreboardingSummaryList } from './preboardingApi';
import { useNavigate, useParams } from 'react-router-dom';
import { usePreboardingTabContext } from './PreboardingTabProvider';



interface SummaryCardProps {
  Id: number;
  Label: string;
  Key: string;
  Value: number;
  Bg: string;
}

const cardWidthVW = 22; // 700px / 1920px * 100vw
const cardHeightVW = 10; // 200px / 1920px * 100vw

const PreboardingSummary = () => {
  const [statusSummaryList, setStatusSummaryList] = useState<SummaryCardProps[]>([]);
  const { tenant } = useParams<{ tenant: string }>();
  const [loading, setLoading] = useState(true);
  const { setSelectedOption,setShowFilter } = usePreboardingTabContext();

  const navigate = useNavigate();

  const fetchSummaryData = async () => {
    try {
      const result = await getPreboardingSummaryList(tenant);
      setStatusSummaryList(result); // Use Data from API response
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };



  useEffect(() => {
    fetchSummaryData();
    setShowFilter('All Candidates');
  }, [tenant]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh" width="100%">
        <CircularProgress />
      </Box>
    );
  }

  // Group rows explicitly
  const firstRow = [
    statusSummaryList.find(item => item.Label === "Total Candidates"),
    statusSummaryList.find(item => item.Label === "Hired"),
    // statusSummaryList.find(item => item.Label === "On Hold"),
    statusSummaryList.find(item => item.Label === "Rejected"),
  ].filter(Boolean) as SummaryCardProps[];

  const secondRow = [
    statusSummaryList.find(item => item.Label === "Offer Pending"),
    statusSummaryList.find(item => item.Label === "Offer Declined"),
    statusSummaryList.find(item => item.Label === "Offer Sent - Awaiting Acceptance"),
    statusSummaryList.find(item => item.Label === "Profile Incomplete"),
  ].filter(Boolean) as SummaryCardProps[];

  const thirdRow = [
    statusSummaryList.find(item => item.Label === "Documents Pending"),
    statusSummaryList.find(item => item.Label === "Pending Document Verification"),
    statusSummaryList.find(item => item.Label === "BGV Pending"),
    statusSummaryList.find(item => item.Label === "BGV Rejected")
  ].filter(Boolean) as SummaryCardProps[];

  const handleCardClick = (option: string, status: string, label: string) => {
  setSelectedOption(option as 'all-candidates');

  // Handle "Total Candidates" case
  if (label === "Total Candidates") {
    setShowFilter("All Candidates");
    navigate(`/${tenant}/preboarding/${option}`, { state: { status } });
    return;
  }

  // Handle "Hired" case
  if (status === "Hired") {
    navigate(`/${tenant}/preboarding/hired-candidates`);
    return;
  }

  // Default case for other statuses
  setShowFilter(`${label} Candidates`);
  navigate(`/${tenant}/preboarding/${option}`, { state: { status } });
};

  return (
    <Box mt={2.5} mr={2.3}>
      {/* First row */}
      <Stack direction="row" spacing={2} mb={2} sx={{ cursor: 'pointer' }}>
        {firstRow.map(item => (
          <Stack  key={item.Label}>
            <Box
              sx={{
                bgcolor: item.Bg,
                borderRadius: 1,
                p: 2,
                minHeight: `${cardHeightVW}vw`,
                width: { md: `29.7vw`, xs: '100%' },
                boxSizing: 'border-box',
              }}
              onClick={() => handleCardClick('all-candidates', item.Key, item.Label)}
            >
              <Typography variant="body1" color={theme.palette.text.secondary}>
                {item.Label}
              </Typography>
              <Typography variant="h4" fontWeight={600} mt={1}>
                {item.Value}
              </Typography>
            </Box>
          </Stack>
        ))}
      </Stack>

      {/* Second row */}
      <Stack direction="row" spacing={2} mb={2} sx={{ cursor: 'pointer' }}>
        {secondRow.map(item => (
          <Stack key={item.Label}>
            <Box
              sx={{
                bgcolor: item.Bg,
                borderRadius: 1,
                p: 2,
                minHeight: `${cardHeightVW}vw`,
                width: { md: `${cardWidthVW}vw`, xs: '100%' },
                boxSizing: 'border-box',
              }}
              onClick={() => handleCardClick('all-candidates', item.Key, item.Label)}
            >
              <Typography variant="body1" color={theme.palette.text.secondary}>
                {item.Label}
              </Typography>
              <Typography variant="h4" fontWeight={600} mt={1}>
                {item.Value}
              </Typography>
            </Box>
          </Stack>
        ))}
      </Stack>

      {/* Third row */}
      <Stack direction="row" spacing={2} sx={{ cursor: 'pointer' }}>
        {thirdRow.map(item => (
          <Stack key={item.Label}>
            <Box
              sx={{
                bgcolor: item.Bg,
                borderRadius: 1,
                p: 2,
                minHeight: `${cardHeightVW}vw`,
                width: { md: `${cardWidthVW}vw`, xs: '100%' },
                boxSizing: 'border-box',
              }}
              onClick={() => handleCardClick('all-candidates', item.Key, item.Label)}
            >
              <Typography variant="body1" color={theme.palette.text.secondary}>
                {item.Label}
              </Typography>
              <Typography variant="h4" fontWeight={600} mt={1}>
                {item.Value}
              </Typography>
            </Box>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

export default PreboardingSummary;